import { Invoice, InvoiceStatus } from '~/models/invoices/invoices';
import { LoanGetRequestDto } from '~/store/apis/dtos/invoices/invoices.dto';
import dayjs from 'dayjs';
import { getNextPaymentInDays } from '~/helpers/payments.helper';
import {
  LoanDto,
  LoanGetResponseDto,
  LoanStatusDto,
} from '~/store/apis/dtos/loan/loan.dto';
import { Loan } from '~/models/loan/loan';
import { ContractStatus } from '~/enums';
import { nordiskaBankId } from '~/constants/constVars';

const getInvoiceStatus = (
  loanStatus: string,
  dueDate: string,
  datePaid: string
): InvoiceStatus => {
  const _dueDate = dayjs(dueDate);
  const now = dayjs();
  if (datePaid) {
    return 'PAID';
  }
  if (loanStatus === ContractStatus.OpenInCollection) {
    return 'COLLECTION';
  }

  if (_dueDate.isBefore(dayjs())) {
    return now.diff(_dueDate, 'days');
  }

  return 'NEW';
};

const hasOverduePayments = (loanDetails: LoanStatusDto[], fromDate?: Date) => {
  const overduePayments = loanDetails?.filter(
    (line) =>
      !line.date_paid &&
      !line.postponed_date &&
      getNextPaymentInDays(new Date(line.due_date), fromDate) < 0
  );

  return overduePayments?.length > 0;
};

export const mapCurrentInvoice = (
  loanLatestLine: LoanStatusDto,
  activeLoan: LoanDto
): Invoice => {
  return {
    invoiceAmount:
      loanLatestLine.invoice?.invoice_total ?? loanLatestLine.total_month,
    invoiceDate: loanLatestLine.invoice!.created,
    invoiceStatus: getInvoiceStatus(
      activeLoan.status ?? '',
      loanLatestLine.due_date ?? '',
      loanLatestLine.date_paid ?? ''
    ),
    dueDate: loanLatestLine.due_date ?? '',
    amortization: loanLatestLine.invoice!.amortization,
    monthlyFee:
      loanLatestLine.invoice?.monthly_interest ??
      loanLatestLine.monthly_interest,
    contractLineId: loanLatestLine.contract_line_id,
    invoiceId: loanLatestLine.invoice!.invoice_id,
    loanId: activeLoan.loan_id,
    lateFee: loanLatestLine.invoice?.letter_fee,
    latePenaltyFee: loanLatestLine.invoice?.penalty,
  };
};
export const mapUpcomingInvoice = (
  upcomingInvoices: LoanStatusDto[],
  activeLoan: any
) =>
  upcomingInvoices.map((invoice) => {
    return {
      id: invoice?.invoice?.invoice_id ?? null,
      contractLineId: invoice.contract_line_id,
      contractId: invoice.contract_id ?? activeLoan.contract_id,
      generated: invoice.invoice?.invoice_id !== undefined,
      amount: invoice.amount,
      dueDate: invoice.due_date,
      monthIndex: dayjs(invoice.due_date).month(),
      dateAuthorized: invoice.date_authorized,
      amortization: invoice.amortization,
      reference: invoice.reference,
      state: getInvoiceStatus(
        activeLoan.status ?? '',
        invoice.due_date,
        invoice.date_authorized
      ),
      lateFee: invoice.invoice?.letter_fee,
      latePenaltyFee: invoice.invoice
        ? invoice.invoice?.penalty
        : activeLoan.penalty_actual,
      monthlyFee: invoice.invoice
        ? invoice.invoice?.monthly_interest
        : invoice.monthly_interest,
      total: invoice.invoice
        ? invoice.invoice?.invoice_total
        : invoice.total_month,
      sentDate: invoice.sent_date,
      paymentDate: invoice.date_paid,
    };
  });

export const mapPaidInvoices = (
  paidInvoices: LoanStatusDto[],
  activeLoan: any
) =>
  paidInvoices.map((invoice) => {
    return {
      id: invoice.invoice?.invoice_id ?? null,
      contractId: activeLoan.loan_id ?? invoice.contract_id,
      contractLineId: invoice.contract_line_id,
      amount: invoice.amount,
      paymentDate: dayjs(invoice.date_paid).format('YYYY-MM-DD'),
      amortization: invoice.invoice?.amortization,
      lateFee: invoice.invoice?.letter_fee,
      latePenaltyFee: invoice.invoice
        ? invoice.invoice?.penalty
        : activeLoan.penalty_actual,
      monthlyFee: invoice.invoice?.monthly_interest,
      total: invoice.invoice?.invoice_total ?? invoice.total_month,
    };
  });

export const mapLoanData = (
  response: LoanGetResponseDto,
  args: LoanGetRequestDto
): Loan | undefined => {
  const { loans } = response.loan;

  const activeLoan = loans?.find((l) => l.company_id === args.companyId);

  if (!activeLoan) {
    return undefined;
  }
  const loanLatestLine = activeLoan?.loan_status
    .filter((c) => c.invoice !== null)
    .sort((a, b) => {
      const aDate = new Date(a.due_date);
      const bDate = new Date(b.due_date);
      if (!a.date_paid && !b.date_paid) {
        return aDate < bDate ? -1 : 1;
      }

      return aDate < bDate ? 1 : -1;
    });

  const upcomingInvoices =
    activeLoan?.loan_status?.filter(
      (c) =>
        !c.date_paid &&
        c.contract_line_id !== loanLatestLine?.[0]?.contract_line_id
    ) ?? [];

  const paidInvoices =
    activeLoan?.loan_status?.filter((c) => c.date_paid) ?? [];

  return {
    hasOverduePayments: hasOverduePayments(activeLoan.loan_status),
    currentInvoice:
      activeLoan && loanLatestLine.length > 0 && loanLatestLine[0].invoice
        ? mapCurrentInvoice(loanLatestLine[0], activeLoan)
        : undefined,
    upcomingInvoices: mapUpcomingInvoice(upcomingInvoices, activeLoan),
    paidInvoices: mapPaidInvoices(paidInvoices, activeLoan),
    amountLeft: activeLoan?.amount_left ?? 0,
    amountPaid: activeLoan.loan_amount - activeLoan.amount_left,
    total: activeLoan.loan_amount,
    terms: activeLoan.loan_terms,
    loanId: activeLoan.loan_id,
    status: activeLoan.status,
    granter:
      activeLoan.granter_bank_id === nordiskaBankId ? 'nordiska' : 'qred',
  };
};
